<template>
  <b-modal
    :id="$attrs.id"
    ref="edit-h1-parcel-items-modal"
    size="xl"
    scrollable
    no-close-on-backdrop
    header-class="h1-parcel-modal-header"
    body-class="h1-parcel-modal-body"
  >
    <template #modal-header="{ close }">
      <div class="w-100">
        <div class="d-flex align-items-center mb-2">
          <h5 class="modal-title mb-0">
            H1 Goods Shipment Details
          </h5>
          <div
            v-if="parcelInfoParts.length > 0"
            class="flex-grow-1 text-center mx-3"
            style="font-size: 14px;"
          >
            <template v-for="(part, index) in parcelInfoParts">
              <span :key="`part-${index}`">
                <strong>{{ part.label }}</strong> {{ part.value }}
              </span>
              <span
                v-if="index < parcelInfoParts.length - 1"
                :key="`sep-${index}`"
                style="margin: 0 0.5em;"
              >
                |
              </span>
            </template>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="close"
            @click="close()"
          >
            ×
          </button>
        </div>
        <ul class="nav nav-tabs nav-fill">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 0 }"
              href="#"
              @click.prevent="activeTab = 0"
            >
              Goods shipment & Declaration details
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 1 }"
              href="#"
              @click.prevent="activeTab = 1"
            >
              Goods items
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 2 }"
              href="#"
              @click.prevent="activeTab = 2"
            >
              Internal notes (not sent to customs)
            </a>
          </li>
          <li
            v-if="isFinnishCustoms"
            class="nav-item"
          >
            <a
              class="nav-link"
              :class="{ active: activeTab === 3 }"
              href="#"
              @click.prevent="activeTab = 3"
            >
              Customs communications
            </a>
          </li>
        </ul>
      </div>
    </template>
    <clear-manually
      style="top: 100px"
      :show="showClearManually"
      :declaration="declaration"
      :shipment-id="shipmentId"
      @close="closeClearManually()"
      @saved="clearManuallySaved()"
    />

    <b-overlay
      :show="isLoading"
      opacity="0.65"
      spinner-variant="primary"
    >
      <b-card-text>
        <div v-show="activeTab === 0">
          <h1-parcel-declaration-details-tab
            ref="declarationDetailsTab"
            :parcel="localParcel"
            :declaration="declaration"
            :company-id="companyId"
            :shipment-id="shipmentId"
            :disabled="disableChanges"
            :is-loading-buyer-data="isLoadingBuyerData"
            :agent-number="agentNumber"
            :contact-person="contactPerson"
            :company-data="companyData"
            :entry-country="shipment && shipment.entry_country"
          />
        </div>

        <div v-show="activeTab === 1">
          <h1-parcel-items-tab
            ref="parcelItemsTab"
            :parcel-items="parcelItems"
            :visible-parcel-items="visibleParcelItems"
            :item-fields="itemFields"
            :disable-changes="disableChanges"
            :countries-options="countriesOptions"
            :shipment-id="shipmentId"
            :parcel-id="parcel.id || null"
            :current-item-index.sync="currentItemIndex"
            :total-items="totalItems"
            :current-page="currentPage"
            :total-pages="totalPages"
            :is-loading-items="isLoadingItems"
            :items-with-issues-count="itemsWithIssuesCount"
            @add-item="addParcelItem"
            @remove-item="removeItem"
            @change-page="handleChangePage"
          />
        </div>

        <div v-show="activeTab === 2">
          <notes-component
            :company-id="companyId"
            :parcel-id="parcel.id"
          />
          <attachment-component
            :company-id="companyId"
            :parcel-id="parcel.id"
          />
        </div>

        <div
          v-if="isFinnishCustoms"
          v-show="activeTab === 3"
        >
          <supporting-documents-component
            v-if="declaration"
            :shipment-id="shipmentId"
            :parcel-id="parcel.id"
            :declaration="declaration"
            :questions="declaration.customs_questions || []"
            :can-send-messages="canSendCustomsMessages"
            :company-id="companyId"
          />
        </div>
      </b-card-text>
    </b-overlay>

    <template #modal-footer>
      <div
        v-if="activeTab === 1"
        class="d-flex w-100 justify-content-between mb-2"
      >
        <b-button
          variant="light"
          @click="$refs.parcelItemsTab.collapseAll()"
        >
          Close goods items
        </b-button>
        <div>
          <b-button
            variant="dark"
            class="ml-2"
            :disabled="currentItemIndex <= 0 && currentPage <= 1"
            @click="goToPreviousItem()"
          >
            Previous goods item
          </b-button>
          <b-button
            variant="dark"
            class="ml-2"
            :disabled="currentItemIndex >= parcelItems.length - 1 && currentPage >= totalPages"
            @click="goToNextItem()"
          >
            Next goods item
          </b-button>
        </div>
      </div>

      <div class="d-flex w-100 justify-content-end">
        <template v-if="declaration">
          <template v-if="!['cleared', 'cleared_manually'].includes(declaration.status)">
            <b-button
              v-if="canEdit && shipmentId"
              variant="secondary"
              class="mr-2"
              @click="clearManually()"
            >
              Clear manually
            </b-button>
            <span
              v-if="shipmentId && !disableChanges"
              v-b-tooltip.hover.top="!isFormReadyToSubmit ? formValidationErrorMessage : ''"
              tabindex="0"
            >
              <b-button
                variant="primary"
                :disabled="isLoading || !isFormReadyToSubmit"
                @click="confirmDeclaration()"
              >
                Submit amendments
                <b-spinner
                  v-if="isLoading"
                  label="Loading..."
                  small
                />
              </b-button>
            </span>
          </template>
        </template>
        <template v-else>
          <span
            v-if="shipmentId && !disableChanges"
            v-b-tooltip.hover.top="!isFormReadyToSubmit ? formValidationErrorMessage : ''"
            tabindex="0"
          >
            <b-button
              variant="primary"
              :disabled="isLoading || !isFormReadyToSubmit"
              @click="confirmDeclaration()"
            >
              Save
              <b-spinner
                v-if="isLoading"
                label="Loading..."
                small
              />
            </b-button>
          </span>
        </template>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BButton, BCardText, BModal, BOverlay, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import H1ParcelDeclarationDetailsTab from '@/views/shipments/declarations/modals/components/h1/H1ParcelDeclarationDetailsTab.vue'
import H1ParcelItemsTab from '@/views/shipments/declarations/modals/components/h1/H1ParcelItemsTab.vue'
import NotesComponent from '@/views/shipments/declarations/modals/components/NotesComponent.vue'
import AttachmentComponent from '@/views/shipments/declarations/modals/components/AttachmentComponent.vue'
import SupportingDocumentsComponent from '@/views/shipments/declarations/modals/components/SupportingDocumentsComponent.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { allCountries } from '@/data/countries'
import ClearManually from '@/views/shipments/declarations/modals/components/ClearManually.vue'
import { EventBus } from '@core/utils/utils'

export default {
  components: {
    BButton,
    BCardText,
    BModal,
    BOverlay,
    BSpinner,
    H1ParcelDeclarationDetailsTab,
    H1ParcelItemsTab,
    NotesComponent,
    AttachmentComponent,
    SupportingDocumentsComponent,
    ClearManually,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    declaration: {
      type: Object,
      default: () => null,
    },
    shipment: {
      type: Object,
      default: () => null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localParcel: null,
      activeTab: 0,
      isLoading: false,
      showClearManually: false,
      isLoadingBuyerData: true,
      buyerData: null,
      agentNumber: null,
      contactPerson: null,
      companyData: null,
      currentItemIndex: 0,
      pageSize: 50,
      currentPage: 1,
      serverTotalItems: null,
      serverTotalPages: null,
      isLoadingItems: false,
      pageItems: null,
      originalPageItems: [],
      countriesOptions: allCountries,
      itemFields: [
        { key: 'show_details', label: '', thStyle: { width: '60px' } },
        { key: 'item_number', label: 'Item nr.', thStyle: { width: '80px' } },
        { key: 'description', label: 'Description', thStyle: { width: '40%' } },
        { key: 'hs_code', label: 'HS Code', thStyle: { width: '150px' } },
        // { key: 'quantity', label: 'Quantity' }, // Hidden - can be unhidden later
        { key: 'value', label: 'Value', thStyle: { width: '120px' } },
        { key: 'actions', label: 'Actions', thStyle: { width: '100px' } },
      ],
    }
  },
  computed: {
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    disableChanges() {
      return this.readonly || !this.canEdit
    },
    isFinnishCustoms() {
      return this.declaration?.customs_country === 'FI'
    },
    canSendCustomsMessages() {
      if (!this.declaration) return false
      if (this.declaration.status === 'dispatch_declaration') {
        return false
      }
      return this.declaration.can_send_customs_message
        || (!this.readonly
          && this.declaration.can_send_amendment
          && this.declaration.can_reply_to_customs_communications)
    },
    companyId() {
      return this.shipment?.company_id || null
    },
    shipmentId() {
      return this.shipment?.id || null
    },
    parcelItems() {
      return this.pageItems !== null ? this.pageItems : (this.localParcel?.items || [])
    },
    visibleParcelItems() {
      return this.parcelItems.filter(item => !item.isHidden)
    },
    totalItems() {
      return this.serverTotalItems !== null ? this.serverTotalItems : this.parcelItems.length
    },
    totalPages() {
      return this.serverTotalPages !== null ? this.serverTotalPages : Math.ceil(this.totalItems / this.pageSize)
    },
    itemsWithIssuesCount() {
      if (this.localParcel?.items_with_issues_count != null) {
        return this.localParcel.items_with_issues_count
      }
      return this.parcelItems.filter(item => item.issues && item.issues.length > 0).length
    },
    isFormReadyToSubmit() {
      if (!this.localParcel) return false
      return this.validateGoodsShipment(this.localParcel).length === 0 && this.itemsWithIssuesCount === 0
    },
    formValidationErrorMessage() {
      if (!this.localParcel) return ''
      const errors = this.validateGoodsShipment(this.localParcel)
      if (errors.length > 0) return errors[0].message
      if (this.itemsWithIssuesCount > 0) return `${this.itemsWithIssuesCount} goods item(s) have validation issues. Expand the goods items tab to see details.`
      return ''
    },
    parcelInfoParts() {
      const infoParts = []

      if (this.localParcel?.tracking_number) {
        infoParts.push({
          label: 'Tracking code:',
          value: this.localParcel.tracking_number,
        })
      }

      if (this.declaration?.lrn) {
        infoParts.push({
          label: 'LRN:',
          value: this.declaration.lrn,
        })
      }

      if (this.localParcel?.house_goods_shipment_tracking_number) {
        infoParts.push({
          label: 'Alternative tracking code:',
          value: this.localParcel.house_goods_shipment_tracking_number,
        })
      }

      if (this.localParcel?.container_code) {
        infoParts.push({
          label: 'Container:',
          value: this.localParcel.container_code,
        })
      }

      if (this.shipment?.document_number) {
        infoParts.push({
          label: 'Shipment number:',
          value: this.shipment.document_number,
        })
      }

      return infoParts
    },
    declarationUserId() {
      return this.localParcel?.low_value_declaration?.user_id || this.$profile().data.user.id
    },
  },
  watch: {
    parcel(newParcel) {
      if (newParcel) {
        this.localParcel = JSON.parse(JSON.stringify(newParcel))
        this.pageItems = null
        this.originalPageItems = []
        this.serverTotalItems = null
        this.serverTotalPages = null
        this.currentPage = 1
        this.initializeAllParcelData()
        if (this.localParcel.id && this.shipmentId) {
          this.fetchItemsFromServer(1)
        }
      }
    },
  },
  mounted() {
    this.localParcel = JSON.parse(JSON.stringify(this.parcel))
    this.initializeAllParcelData()
    // Remove before adding to guarantee no duplicate listeners on remount
    EventBus.$off('buyer-data-loader', this.handleBuyerDataLoader)
    EventBus.$off('update-buyer', this.handleUpdateBuyer)
    EventBus.$on('buyer-data-loader', this.handleBuyerDataLoader)
    EventBus.$on('update-buyer', this.handleUpdateBuyer)
    this.fetchAgentNumber()
    this.fetchContactPerson()
    if (this.localParcel.id && this.shipmentId) {
      this.fetchItemsFromServer(1)
    }
  },
  beforeDestroy() {
    EventBus.$off('buyer-data-loader', this.handleBuyerDataLoader)
    EventBus.$off('update-buyer', this.handleUpdateBuyer)
  },
  methods: {
    handleBuyerDataLoader(loading) {
      this.isLoadingBuyerData = loading
    },
    handleUpdateBuyer(buyer) {
      this.buyerData = buyer
    },
    async fetchContactPerson() {
      try {
        const response = await this.$http.get(`/v1/users/${this.declarationUserId}`)
        const user = response.data.user || null
        this.contactPerson = user
        if (user?.last_login_company_id) {
          await this.fetchCompanyData(user.last_login_company_id)
        }
      } catch (e) {
        this.contactPerson = null
      }
    },
    async fetchCompanyData(companyId) {
      try {
        const response = await this.$http.get(`/v1/companies/${companyId}`)
        this.companyData = response.data || null
      } catch (e) {
        this.companyData = null
      }
    },
    async fetchAgentNumber() {
      try {
        const response = await this.$http.get(`/v1/user/${this.declarationUserId}/customs/ee`)
        this.agentNumber = response.data.agentNumber || null
      } catch (e) {
        this.agentNumber = null
      }
    },
    initializeParcelExporter() {
      if (this.localParcel && !this.localParcel.exporter) {
        this.$set(this.localParcel, 'exporter', {
          registry_number: '',
          name: '',
          country: null,
          street_and_number: '',
          city: '',
          postal_code: '',
        })
      }
    },
    initializeParcelImporter() {
      if (this.localParcel && !this.localParcel.importer) {
        this.$set(this.localParcel, 'importer', {
          registry_number: '',
          name: '',
          country: '',
          street_and_number: '',
          city: '',
          postal_code: '',
        })
      }
    },
    initializeParcelPaymentData() {
      if (this.localParcel && !this.localParcel.payment_data) {
        this.$set(this.localParcel, 'payment_data', {
          person_paying_customs_duty: '',
          prefer_individual_guarantee: '',
          use_deposit_for_individual_guarantee: '',
          skip_waiting_quota: '',
        })
      }
    },
    initializeParcelAgent() {
      if (this.localParcel && !this.localParcel.agent) {
        this.$set(this.localParcel, 'agent', {
          type_of_representation: 3,
          identification: '',
          name: '',
          agent_number: '',
        })
      } else if (this.localParcel && this.localParcel.agent && !this.localParcel.agent.type_of_representation) {
        this.$set(this.localParcel.agent, 'type_of_representation', 3)
      }
    },
    initializeParcelDeclarant() {
      if (this.localParcel && !this.localParcel.declarant) {
        this.$set(this.localParcel, 'declarant', {
          submitter_id: '',
          submitter_name: '',
          identification: '',
          name: '',
          country: null,
          full_address: '',
          postcode: '',
          person_name: '',
          phone_number: '',
          email_address: '',
        })
      }
    },
    initializeParcelTransportData() {
      if (this.localParcel && !this.localParcel.transport_data) {
        this.$set(this.localParcel, 'transport_data', {
          border_transport_means_mode: null,
          border_transport_means_registration_nationality: null,
          arrival_transport_means_mode: null,
          arrival_transport_means_identification_type: null,
          arrival_transport_means_identification: '',
          export_country: null,
          destination_country: null,
          destination_region: '',
        })
      }
    },
    initializeParcelContainers() {
      if (this.localParcel && (!this.localParcel.maritime_containers || this.localParcel.maritime_containers.length === 0)) {
        this.$set(this.localParcel, 'maritime_containers', [{
          container_identification_number: '',
          goods_item_number: '',
        }])
      }
    },
    initializeParcelTransactionData() {
      if (this.localParcel && !this.localParcel.transaction_data) {
        this.$set(this.localParcel, 'transaction_data', {
          invoice_amount: '',
          invoice_currency: null,
          total_gross_mass: '',
          total_net_weight: '',
          transaction_nature: null,
        })
      }
    },
    initializeParcelDeliveryTerms() {
      if (this.localParcel && !this.localParcel.delivery_terms) {
        this.$set(this.localParcel, 'delivery_terms', {
          trade_terms_condition: null,
          trade_terms_location_name: '',
          trade_terms_country: null,
          trade_terms_text: '',
        })
      }
    },
    initializeParcelCostsDistribution() {
      if (this.localParcel && !this.localParcel.costs_distribution_data) {
        this.$set(this.localParcel, 'costs_distribution_data', {
          costs_distribution_method: null,
          additions_deductions: [],
        })
      } else if (
        this.localParcel
        && this.localParcel.costs_distribution_data
        && !this.localParcel.costs_distribution_data.additions_deductions
      ) {
        this.$set(this.localParcel.costs_distribution_data, 'additions_deductions', [])
      }
    },
    initializeParcelPreviousDocuments() {
      if (this.localParcel && (!this.localParcel.previous_documents || this.localParcel.previous_documents.length === 0)) {
        this.$set(this.localParcel, 'previous_documents', [{
          document_type: '',
          reference_number: '',
          transport_document_reference: '',
          date: '',
        }])
      }
    },
    initializeParcelTransportDocuments() {
      if (this.localParcel && !this.localParcel.transport_documents) {
        this.$set(this.localParcel, 'transport_documents', [])
      }
    },
    initializeParcelAdditionalFiscalReferences() {
      if (this.localParcel && (!this.localParcel.additional_fiscal_references || this.localParcel.additional_fiscal_references.length === 0)) {
        this.$set(this.localParcel, 'additional_fiscal_references', [{
          role: '',
          vat_identification_number: '',
        }])
      }
    },
    initializeParcelSupportingDocuments() {
      if (this.localParcel && (!this.localParcel.supporting_documents || this.localParcel.supporting_documents.length === 0)) {
        this.$set(this.localParcel, 'supporting_documents', [{
          document_type: '',
          reference_number: '',
          date: '',
          document_line_item_number: '',
          issuing_authority: '',
          file: null,
          file_id: '',
          file_name: '',
          file_url: '',
        }])
      }
    },
    initializeAllParcelData() {
      this.initializeGoodsData()
      this.initializeParcelExporter()
      this.initializeParcelImporter()
      this.initializeParcelPaymentData()
      this.initializeParcelAgent()
      this.initializeParcelDeclarant()
      this.initializeParcelTransportData()
      this.initializeParcelContainers()
      this.initializeParcelTransactionData()
      this.initializeParcelDeliveryTerms()
      this.initializeParcelCostsDistribution()
      this.initializeParcelPreviousDocuments()
      this.initializeParcelTransportDocuments()
      this.initializeParcelSupportingDocuments()
      this.initializeParcelAdditionalFiscalReferences()
    },
    initializeGoodsData() {
      if (this.localParcel && this.localParcel.items) {
        this.initializeItemsSubObjects(this.localParcel.items)
      }
    },
    initializeItemsSubObjects(items) {
      (items || []).forEach(item => {
        this.$set(item, 'isHidden', false)
        if (!item.goods_data) {
          this.$set(item, 'goods_data', {
            sequence_on_shipment: '',
            shipment_identification: '',
            acceptance_date: '',
            trader_assigned_reference: '',
            current_procedure: '',
            previous_procedure: '',
            additional_procedure: '',
            cus_code: '',
            export_country: null,
            destination_country: null,
            destination_region: '',
            preferential_origin_country: null,
            duty_regime: '',
            quota_order: '',
            taric_additional: '',
            national_additional: '',
          })
        }
        if (item.goods_data.commodity_description == null && item.description != null) {
          this.$set(item.goods_data, 'commodity_description', item.description)
        }
        if (item.goods_data.commodity_code == null && item.hs_code != null) {
          this.$set(item.goods_data, 'commodity_code', item.hs_code)
        }
        if (item.goods_data.gross_mass == null && item.gross_weight != null) {
          this.$set(item.goods_data, 'gross_mass', item.gross_weight)
        }
        if (item.goods_data.net_weight == null && item.net_weight != null) {
          this.$set(item.goods_data, 'net_weight', item.net_weight)
        }
        if (item.goods_data.origin_country == null && item.origin_country != null) {
          this.$set(item.goods_data, 'origin_country', item.origin_country)
        }
        if (!item.transaction_data) {
          const gd = item.goods_data || {}
          this.$set(item, 'transaction_data', {
            item_charge: item.value || '',
            customs_valuation_method: gd.customs_valuation_method || '',
            valuation_adjustment_addition: gd.valuation_adjustment_addition || '',
            statistical_value: gd.statistical_value || '',
            transaction_nature: gd.transaction_nature || '',
          })
        } else if (item.transaction_data.item_charge == null && item.value != null) {
          this.$set(item.transaction_data, 'item_charge', item.value)
        }
        const normalizedItem = this.normalizeH1Item(item)
        this.$set(item, 'description', normalizedItem.description)
        this.$set(item, 'hs_code', normalizedItem.hs_code)
        this.$set(item, 'value', normalizedItem.value)
        this.$set(item, 'gross_weight', normalizedItem.gross_weight)
        this.$set(item, 'net_weight', normalizedItem.net_weight)
        this.$set(item, 'origin_country', normalizedItem.origin_country)
        if (!item.supplementary_units) {
          this.$set(item, 'supplementary_units', [{
            supplementary_unit: '',
            measurement_qualifier: '',
            quantity: '',
          }])
        }
        if (!item.additions_deductions) {
          this.$set(item, 'additions_deductions', [{
            code: '',
            amount: '',
            currency: '',
          }])
        }
        if (!item.packaging) {
          this.$set(item, 'packaging', [{
            type_of_packages: '',
            number_of_packages: '',
            shipping_marks: '',
          }])
        }
        if (!item.supporting_documents || item.supporting_documents.length === 0) {
          this.$set(item, 'supporting_documents', [{
            document_type: '',
            reference_number: '',
            date: '',
            supplementary_measurement_unit: '',
            measurement_qualifier: '',
            quantity: '',
            currency: '',
            document_line_item_number: '',
            amount: '',
            issuing_authority: '',
            file: null,
            file_id: '',
            file_name: '',
            file_url: '',
          }])
        }
      })
    },
    newItemTemplate() {
      return {
        description: '',
        hs_code: '',
        quantity: 0,
        value: 0,
        isHidden: false,
        goods_data: {
          goods_item_number: '',
          sequence_on_shipment: '',
          shipment_identification: '',
          acceptance_date: '',
          trader_assigned_reference: '',
          current_procedure: '',
          previous_procedure: '',
          additional_procedure: '',
          cus_code: '',
          export_country: null,
          destination_country: null,
          destination_region: '',
          preferential_origin_country: null,
          duty_regime: '',
          quota_order: '',
          taric_additional: '',
          national_additional: '',
        },
        transaction_data: {
          item_charge: '',
          customs_valuation_method: '',
          valuation_adjustment_addition: '',
          statistical_value: '',
          transaction_nature: '',
        },
        supplementary_units: [{
          supplementary_unit: '',
          measurement_qualifier: '',
          quantity: '',
        }],
        additions_deductions: [{
          code: '',
          amount: '',
          currency: '',
        }],
        packaging: [{
          type_of_packages: '',
          number_of_packages: '',
          shipping_marks: '',
        }],
        supporting_documents: [{
          document_type: '',
          reference_number: '',
          date: '',
          supplementary_measurement_unit: '',
          measurement_qualifier: '',
          quantity: '',
          currency: '',
          document_line_item_number: '',
          amount: '',
          issuing_authority: '',
          file: null,
          file_id: '',
          file_name: '',
          file_url: '',
        }],
      }
    },
    addParcelItem() {
      const newItem = this.newItemTemplate()
      if (this.usesServerPagination()) {
        this.pageItems.push(newItem)
        this.serverTotalItems += 1
      } else {
        if (!this.localParcel.items) {
          this.$set(this.localParcel, 'items', [])
        }
        this.localParcel.items.push(newItem)
      }
    },
    removeItem(item) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this goods item?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (!value) return
        if (!item.id) {
          if (this.usesServerPagination()) {
            this.pageItems = this.pageItems.filter(x => x !== item)
            this.serverTotalItems = Math.max(0, this.serverTotalItems - 1)
          } else if (this.localParcel.items) {
            const index = this.localParcel.items.indexOf(item)
            if (index !== -1) {
              this.localParcel.items.splice(index, 1)
            }
          }
          return
        }
        this.isLoadingItems = true
        this.$http.delete(`/v1/shipments/${this.shipmentId}/goods-shipments/${this.localParcel.id}/goods-items/${item.id}`).then(() => {
          if (this.usesServerPagination()) {
            this.pageItems = this.pageItems.filter(x => x.id !== item.id)
            this.originalPageItems = this.originalPageItems.filter(x => x.id !== item.id)
            this.serverTotalItems = Math.max(0, this.serverTotalItems - 1)
          } else if (this.localParcel.items) {
            const index = this.localParcel.items.findIndex(x => x.id === item.id)
            if (index !== -1) {
              this.localParcel.items.splice(index, 1)
            }
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Goods item removed',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        }).catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.response?.data?.message || error.message || 'Failed to remove goods item',
            },
          })
        }).finally(() => {
          this.isLoadingItems = false
        })
      })
    },

    /**
     * Shared validation for goods shipment data.
     * Returns an array of { message } error objects — empty array means valid.
     * Used by both isFormReadyToSubmit (button state) and confirmDeclaration (toast errors).
     */
    validateGoodsShipment(parcel) {
      const errors = []

      // --- Document-level checks (filter to non-empty rows first) ---
      // N703 is handled separately via tracking_number, exclude it from this check
      const nonN703TransportDocs = (parcel.transport_documents || []).filter(d => {
        const type = d && d.transport_document_type != null && typeof d.transport_document_type === 'object' && 'value' in d.transport_document_type ? d.transport_document_type.value : (d && d.transport_document_type)
        return !(type != null && String(type).toUpperCase() === 'N703')
      })
      const nonEmptyTransportDocs = nonN703TransportDocs.filter(
        d => d.id || (d.transport_document_type && d.transport_document_type !== '') || (d.transport_document_reference_number && d.transport_document_reference_number !== ''),
      )
      if (nonEmptyTransportDocs.some(d => !d.id && (!d.transport_document_type || d.transport_document_type === ''))) {
        errors.push({ message: 'Transport document type is required for all transport documents.' })
      }

      const nonEmptyPreviousDocs = (parcel.previous_documents || []).filter(
        d => d.id || (d.document_type && d.document_type !== '') || (d.reference_number && d.reference_number !== '') || (d.transport_document_reference && d.transport_document_reference !== '') || (d.date && d.date !== ''),
      )
      if (nonEmptyPreviousDocs.some(d => !d.id && (!d.document_type || d.document_type === ''))) {
        errors.push({ message: 'Document type is required for all previous documents.' })
      }

      const nonEmptySupportingDocs = (parcel.supporting_documents || []).filter(
        d => d.id || (d.document_type && d.document_type !== '') || (d.reference_number && d.reference_number !== '') || (d.date && d.date !== ''),
      )
      if (nonEmptySupportingDocs.some(d => !d.id && (!d.document_type || d.document_type === ''))) {
        errors.push({ message: 'Document type is required for all supporting documents.' })
      }

      // Costs additions/deductions: AK is handled separately via transport_cost fields,
      // so only non-AK rows the user explicitly added need to be fully filled in.
      const additionsDeductions = (parcel.costs_distribution_data && parcel.costs_distribution_data.additions_deductions) || []
      const nonAkAdditions = additionsDeductions.filter(a => {
        const code = a && a.code != null && typeof a.code === 'object' && 'value' in a.code ? a.code.value : (a && a.code)
        return !(code != null && String(code).toUpperCase() === 'AK')
      })
      const hasIncompleteAddition = nonAkAdditions.some(a => {
        const hasAnyData = !!(a.code || a.amount || a.currency)
        return hasAnyData && !(a.code && a.amount !== '' && a.amount !== null && a.amount !== undefined && a.currency)
      })
      if (hasIncompleteAddition) {
        errors.push({ message: 'Code, amount and currency are all required for each addition/deduction row.' })
      }

      return errors
    },
    async confirmDeclaration() {
      this.isLoading = true

      const isValid = await this.$refs.declarationDetailsTab.validateAll()
      if (!isValid) {
        this.isLoading = false
        return
      }

      const areItemsValid = await this.$refs.parcelItemsTab.validateAll()
      if (!areItemsValid) {
        this.isLoading = false
        return
      }

      if (this.declaration && this.declaration.id && !['cleared', 'cleared_manually'].includes(this.declaration.status)) {
        const result = await this.$bvModal.msgBoxConfirm('You have changed declaration data, confirm to send amendment to customs.', {
          title: 'Confirm Amendment',
          okVariant: 'primary',
          okTitle: 'Send amendment',
          cancelTitle: 'Cancel',
          centered: true,
        })
        if (!result) {
          this.isLoading = false
          return
        }
      }

      // Strip empty default rows to avoid null constraint violations on the backend
      const payload = {
        ...this.localParcel,
        // N703 is stored only on tracking_number — never in transport_documents
        transport_documents: (this.localParcel.transport_documents || []).filter(
          d => {
            const type = d && d.transport_document_type != null && typeof d.transport_document_type === 'object' && 'value' in d.transport_document_type ? d.transport_document_type.value : (d && d.transport_document_type)
            if (type != null && String(type).toUpperCase() === 'N703') {
              return false
            }
            return d.id || (d.transport_document_type && d.transport_document_type !== '') || (d.transport_document_reference_number && d.transport_document_reference_number !== '')
          },
        ),
        previous_documents: (this.localParcel.previous_documents || []).filter(
          d => d.id || (d.document_type && d.document_type !== '') || (d.reference_number && d.reference_number !== '') || (d.transport_document_reference && d.transport_document_reference !== '') || (d.date && d.date !== ''),
        ),
        supporting_documents: (this.localParcel.supporting_documents || []).filter(
          d => d.id || (d.document_type && d.document_type !== '') || (d.reference_number && d.reference_number !== '') || (d.date && d.date !== ''),
        ).map(d => { const { file, ...rest } = d; return rest }),
        // Items are managed granularly via POST/PUT/DELETE /goods-items[/{id}] (see saveCurrentPage()
        // below), so when server pagination is active we don't bundle them into this payload at all —
        // only fall back to the embedded array here if pagination never loaded (e.g. request failed).
        ...(this.usesServerPagination() ? {} : {
          items: (this.localParcel.items || []).map(item => {
            const normalizedItem = this.normalizeH1Item(item)
            const {
              fiscal_references, transaction_data: transactionData, isHidden, _showDetails: showDetails, ...itemWithoutFiscalRefs
            } = normalizedItem
            return {
              ...itemWithoutFiscalRefs,
              goods_data: {
                ...(normalizedItem.goods_data || {}),
              },
              transaction_data: {
                item_charge: transactionData?.item_charge ?? null,
                customs_valuation_method: transactionData?.customs_valuation_method ?? null,
                valuation_adjustment_addition: transactionData?.valuation_adjustment_addition ?? null,
                statistical_value: transactionData?.statistical_value ?? null,
                transaction_nature: transactionData?.transaction_nature ?? null,
              },
              supporting_documents: (item.supporting_documents || []).filter(
                d => d.id || (d.document_type && d.document_type !== '') || (d.reference_number && d.reference_number !== '') || (d.date && d.date !== ''),
              ).map(d => { const { file, ...rest } = d; return rest }),
            }
          }),
        }),
        maritime_containers: (this.localParcel.maritime_containers || []).filter(
          c => c.id || (c.container_identification_number && c.container_identification_number !== ''),
        ),
        costs_distribution_data: this.localParcel.costs_distribution_data ? {
          ...this.localParcel.costs_distribution_data,
          // AK is stored only on transport_cost / transport_cost_currency — never in additions_deductions
          additions_deductions: (this.localParcel.costs_distribution_data.additions_deductions || []).filter(
            a => {
              const code = a && a.code != null && typeof a.code === 'object' && 'value' in a.code ? a.code.value : (a && a.code)
              if (code != null && String(code).toUpperCase() === 'AK') {
                return false
              }
              return a.id || (a.code && a.code !== '') || (a.amount && a.amount !== '')
            },
          ),
        } : this.localParcel.costs_distribution_data,
        additional_fiscal_references: (this.localParcel.additional_fiscal_references || []).filter(
          r => r.id || (r.role && r.role !== '') || (r.vat_identification_number && r.vat_identification_number !== ''),
        ),
        // Flatten buyer data into top-level fields expected by the backend
        buyer: undefined,
        ...(this.buyerData && (this.buyerData.name || this.buyerData.registry_number) ? {
          buyer_name: this.buyerData.name || null,
          buyer_registry_number: this.buyerData.registry_number || null,
          buyer_street_and_number: this.buyerData.street_and_number || null,
          buyer_postal_code: this.buyerData.postal_code || null,
          buyer_city: this.buyerData.city || null,
          buyer_region: this.buyerData.region || null,
          buyer_country: this.buyerData.country || null,
        } : {}),
      }
      const validationErrors = this.validateGoodsShipment(payload)
      if (validationErrors.length > 0) {
        this.isLoading = false
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Error', icon: 'AlertTriangleIcon', variant: 'danger', text: validationErrors[0].message },
        })
        return
      }

      try {
        // Persist added/edited items for the current page granularly before anything else,
        // so the amendment trigger (which reads items from the DB) picks them up.
        if (this.usesServerPagination()) {
          const savedOk = await this.saveCurrentPage()
          if (!savedOk) {
            this.isLoading = false
            return
          }
          if (this.itemsWithIssuesCount > 0) {
            this.isLoading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: `${this.itemsWithIssuesCount} goods item(s) have validation issues. Expand the goods items tab to see details.`,
              },
            })
            return
          }
        }

        // Normalize v-select object fields: extract .value when the stored value is still an {label, value} object
        const extractVal = v => (v && typeof v === 'object' && 'value' in v ? v.value : v)
        if (payload.transport_data) {
          const td = payload.transport_data
          td.container = extractVal(td.container)
          td.export_country = extractVal(td.export_country)
          td.destination_country = extractVal(td.destination_country)
          td.border_transport_means_mode = extractVal(td.border_transport_means_mode)
          td.arrival_transport_means_mode = extractVal(td.arrival_transport_means_mode)
          td.arrival_transport_means_identification_type = extractVal(td.arrival_transport_means_identification_type)
          td.border_transport_means_registration_nationality = extractVal(td.border_transport_means_registration_nationality)
        }
        if (payload.delivery_terms) {
          const dt = payload.delivery_terms
          dt.trade_terms_country = extractVal(dt.trade_terms_country)
          dt.trade_terms_condition = extractVal(dt.trade_terms_condition)
        }
        // Steps 1 & 2: Save goods shipment data + update declaration-level fields in parallel
        const savePromises = [
          this.$http.put(`/v1/shipments/${this.shipmentId}/goods-shipments/${this.localParcel.id}`, payload),
        ]

        if (this.declaration && this.declaration.id) {
          const declarationPayload = {
            warehouse_id: this.declaration.warehouse_id || null,
            inland_mode_of_transport: payload.transport_data?.arrival_transport_means_mode || null,
            mode_of_transport_at_border: payload.transport_data?.border_transport_means_mode || null,
            previous_documents: payload.previous_documents || [],
          }
          savePromises.push(
            this.$http.put(
              `/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations/${this.declaration.id}`,
              declarationPayload,
            ),
          )
        }

        await Promise.all(savePromises)

        // Step 3: Trigger status transition (re-dispatch for errored/draft declarations)
        if (this.declaration && this.declaration.id && !['cleared', 'cleared_manually'].includes(this.declaration.status)) {
          await this.$http.put(`/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations`, {
            goodsShipments: [this.localParcel.id],
          })
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Goods shipment updated successfully',
          },
        })

        this.$root.$emit('bv::hide::modal', this.$attrs.id)
        EventBus.$emit('update-parcel-items', this.localParcel.id)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || error.message || 'Failed to update goods shipment',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    clearManually() {
      this.showClearManually = true
    },
    closeClearManually() {
      this.showClearManually = false
    },
    clearManuallySaved() {
      this.showClearManually = false
      this.$root.$emit('bv::hide::modal', this.$attrs.id)
    },
    fetchItemsFromServer(page) {
      this.isLoadingItems = true
      return this.$http.get(`/v1/shipments/${this.shipmentId}/goods-shipments/${this.localParcel.id}/goods-items`, {
        params: { page, per_page: this.pageSize, with_issues_first: 1 },
      }).then(response => {
        this.pageItems = response.data.data
        this.initializeItemsSubObjects(this.pageItems)
        this.originalPageItems = JSON.parse(JSON.stringify(this.pageItems))
        this.serverTotalItems = response.data.meta.total
        this.serverTotalPages = response.data.meta.last_page
        this.currentPage = response.data.meta.current_page
        if (response.data.meta.items_with_issues_count != null) {
          this.$set(this.localParcel, 'items_with_issues_count', response.data.meta.items_with_issues_count)
        }
      }).catch(() => {
        // Fall back to the embedded items array on error (e.g. endpoint not available)
      }).finally(() => {
        this.isLoadingItems = false
      })
    },
    usesServerPagination() {
      return this.serverTotalItems !== null
    },
    mapValidationErrors(errors) {
      const fieldKeys = ['hs_code', 'value', 'description', 'net_weight', 'origin_country', 'duty_regime', 'customs_valuation_method', 'current_procedure', 'previous_procedure', 'additional_procedure']
      return Object.entries(errors).map(([field, messages]) => {
        const matched = fieldKeys.find(key => field.endsWith(key))
        return {
          field: matched || field,
          type: 'danger',
          message: Array.isArray(messages) ? messages[0] : messages,
        }
      })
    },
    // Strip UI-only fields (isHidden/_showDetails toggled purely by row expand/collapse)
    // before comparing dirty state or sending the item to the backend.
    stripUiOnlyFields(item) {
      const {
        isHidden, _showDetails: showDetails, ...rest
      } = item
      return rest
    },
    normalizeH1Item(item) {
      const goodsData = item.goods_data || {}
      const transactionData = item.transaction_data || {}

      return {
        ...item,
        description: goodsData.commodity_description ?? item.description ?? '',
        hs_code: goodsData.commodity_code ?? item.hs_code ?? '',
        value: transactionData.item_charge ?? item.value ?? null,
        gross_weight: goodsData.gross_mass ?? item.gross_weight ?? null,
        net_weight: goodsData.net_weight ?? item.net_weight ?? null,
        origin_country: goodsData.origin_country ?? item.origin_country ?? null,
      }
    },
    buildItemPayload(item) {
      const payload = this.normalizeH1Item(this.stripUiOnlyFields(item))
      const hasValue = value => value !== null && value !== undefined && value !== ''
      const supportingDocumentFields = [
        'document_type',
        'reference_number',
        'date',
        'supplementary_measurement_unit',
        'measurement_qualifier',
        'quantity',
        'currency',
        'document_line_item_number',
        'amount',
        'issuing_authority',
        'file_id',
      ]

      return {
        ...payload,
        packaging: (payload.packaging || []).filter(
          row => row.id || ['type_of_packages', 'number_of_packages', 'shipping_marks']
            .some(field => hasValue(row[field])),
        ),
        supporting_documents: (payload.supporting_documents || []).filter(
          row => row.id || supportingDocumentFields.some(field => hasValue(row[field])),
        ).map(row => {
          const {
            file, file_name: fileName, file_url: fileUrl, ...document
          } = row
          return document
        }),
      }
    },
    saveItem(item) {
      const base = `/v1/shipments/${this.shipmentId}/goods-shipments/${this.localParcel.id}/goods-items`
      // Clear stale validation issues before re-submitting
      this.$set(item, 'issues', [])
      const payload = this.buildItemPayload(item)
      const request = item.id
        ? this.$http.put(`${base}/${item.id}`, payload)
        : this.$http.post(base, payload)
      return request.then(response => {
        const saved = response.data.data ?? response.data
        if (saved && saved.id && !item.id) {
          this.$set(item, 'id', saved.id)
        }
      }).catch(error => {
        const errors = error.response?.data?.errors
        if (errors) {
          this.$set(item, 'issues', this.mapValidationErrors(errors))
        }
        throw error
      })
    },
    // Persist only the current page's added/changed items (called before the parcel-level save,
    // and before navigating away from the current page so edits are never silently lost).
    async saveCurrentPage() {
      if (!this.usesServerPagination()) {
        return true
      }
      const requests = []
      this.pageItems.forEach(item => {
        if (!item.id) {
          requests.push(this.saveItem(item))
          return
        }
        const original = this.originalPageItems.find(o => o.id === item.id)
        const normalizedItem = JSON.stringify(this.buildItemPayload(item))
        const normalizedOriginal = original ? JSON.stringify(this.buildItemPayload(original)) : null
        if (!original || normalizedItem !== normalizedOriginal) {
          requests.push(this.saveItem(item))
        }
      })
      const results = await Promise.allSettled(requests)
      const failed = results.filter(r => r.status === 'rejected')
      if (failed.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: `Failed to save ${failed.length} goods item(s). Please check the goods items tab and try again.`,
          },
        })
        return false
      }
      // Re-fetch the current page so ids/issues/items_with_issues_count reflect the
      // authoritative post-save server state rather than a client-derived approximation.
      await this.fetchItemsFromServer(this.currentPage)
      return true
    },
    handleChangePage(page) {
      this.saveCurrentPage().then(savedOk => {
        if (savedOk) {
          this.fetchItemsFromServer(page)
        }
      })
    },
    async goToPreviousItem() {
      if (this.currentItemIndex > 0) {
        this.$refs.parcelItemsTab.expandItem(this.currentItemIndex - 1)
      } else if (this.currentPage > 1) {
        await this.saveCurrentPage()
        await this.fetchItemsFromServer(this.currentPage - 1)
        this.$nextTick(() => this.$refs.parcelItemsTab.expandItem(this.parcelItems.length - 1))
      }
    },
    async goToNextItem() {
      if (this.currentItemIndex < this.parcelItems.length - 1) {
        this.$refs.parcelItemsTab.expandItem(this.currentItemIndex + 1)
      } else if (this.currentPage < this.totalPages) {
        await this.saveCurrentPage()
        await this.fetchItemsFromServer(this.currentPage + 1)
        this.$nextTick(() => this.$refs.parcelItemsTab.expandItem(0))
      }
    },
  },
}
</script>

<style scoped>
.nav-tabs {
  background-color: #F2F2F2;
  margin-bottom: 0;
}

.nav-item .nav-link {
  text-decoration: underline;
}

.nav-item .nav-link.active {
  background-color: #232756;
  color: white;
  text-decoration: none;
}
</style>
